import { ApiError } from '@app/api/errors';
import Page from '@app/components/layout/page/page';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetSites } from '@shared/api';
import { useCreateCompanyReport } from '@shared/api/hooks/ccf/reports';
import { useGetValidReportingPeriods } from '@shared/api/hooks/reports/periods';
import Text from '@shared/components/content/text';
import SelectFormField from '@shared/components/form/select-form-field';
import GuideTemplate from '@shared/components/guide-template';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { object, z } from 'zod';
import { ccfRoutes } from '../../ccf-routes';
import { formatReportingYearRange } from '../utils';

export const reportSetupSchema = object({
  reportingYear: z.string().min(1, 'Select a reporting year'),
});

export default function CCFSetupPeriod() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: reportingYears, isLoading: isLoadingReportingPeriods } =
    useGetValidReportingPeriods();

  const { mutateAsync: createCompanyReport, isLoading: creatingReport } =
    useCreateCompanyReport();
  const { data: sitesResponse, isLoading: isLoadingSites } = useGetSites({});
  const hasSites =
    !isLoadingSites &&
    sitesResponse?.results &&
    sitesResponse.results.length > 0;

  const form = useForm<z.infer<typeof reportSetupSchema>>({
    resolver: zodResolver(reportSetupSchema),
  });

  const onSubmit = async (data: z.infer<typeof reportSetupSchema>) => {
    try {
      const { uuid } = await createCompanyReport({
        data: {
          timePeriodName: data.reportingYear,
        },
      });

      if (hasSites) {
        navigate(
          generatePath(ccfRoutes.COMPANY_REPORT, {
            report_uuid: uuid,
          })
        );
      } else {
        navigate(
          generatePath(ccfRoutes.COMPANY_REPORT_SETUP_SITES, {
            report_uuid: uuid,
          })
        );
      }
    } catch (error) {
      sentry.log(error);

      let description =
        'Please try again or get in touch if the error persists';

      if (
        error instanceof ApiError &&
        error.type === 'company-report-overlap'
      ) {
        description = 'This reporting period overlaps with an existing report';
      }

      toast({
        title: 'Error creating your report',
        variant: 'destructive',
        description,
      });
    }
  };

  return (
    <Page name="Report setup">
      <GuideTemplate
        title="Your reporting period"
        description={
          <div>
            <Text>
              Select which reporting year you would like to create a company
              report for. Every reporting year starts on the same day each year,
              determined by the reporting period set on your account. You can
              only create one report per year.
            </Text>
          </div>
        }
        primaryActionProps={{
          disabled: creatingReport,
          loading: creatingReport,
          onClick: () => form.handleSubmit(onSubmit)(),
          'data-testid': 'set-year-button',
        }}
        secondaryActionProps={{
          disabled: creatingReport,
          onClick: () =>
            navigate(generatePath(ccfRoutes.COMPANY_REPORT_SETUP_WELCOME)),
        }}
        primaryActionText="Set year"
        secondaryActionText="Go back"
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-80 min-w-[240px] "
          >
            <SelectFormField
              name="reportingYear"
              label="Reporting year"
              loading={isLoadingReportingPeriods}
              testId="reporting-year-select"
              options={
                reportingYears?.map((year) => ({
                  value: year.name,
                  label: formatReportingYearRange(year),
                  disabled: year.reportCreated,
                })) || []
              }
            />
          </form>
        </Form>
      </GuideTemplate>
    </Page>
  );
}
